import React, {useState} from 'react'
import Text from '../../components/text/Text'
import WizSelect from '../../components/select/WizSelect'
import WizButton from '../../components/button/WizButton'
import {ReactComponent as IcGuide} from '../../assets/images/ic_guide.svg'
import ContentBlockBuilder from './ContentBlockBuilder'
import ContentBlocksInlinePreview from './ContentBlocksInlinePreview'
import VariableGuideModal from './VariableGuideModal'
import BlockTypeGuideModal from './BlockTypeGuideModal'
import {
    AI_FEED_CATEGORY_OPTIONS,
    allCaseVariables,
    allowedVariableNames,
    caseVariablesFor,
    EDITOR_CONTRACT,
    IEditorContract,
} from './constants'
import {AiFeedCategory, IFormBody} from './types'
import {extractVariableTokens, formBodyToBlocks, fromContentBlockJSON} from './utils'

interface IProps {
    className?: string
    view: 'create' | 'edit'
    body: IFormBody
    setBody: React.Dispatch<React.SetStateAction<IFormBody>>
    // 카테고리 선택지 — 미지정 시 전체 목록. 생성 화면에서는 계약(kinds) 기반 canonical 목록을 주입한다.
    categoryOptions?: {id: AiFeedCategory; value: string}[]
    // live /kinds 계약 — 검증/변수 목록의 단일 소스. 미지정 시 폴백 상수.
    contract?: IEditorContract
    onSubmit: () => void
    onCancel: () => void
}

const FeedForm: React.FC<IProps> = ({
    className,
    view,
    body,
    setBody,
    categoryOptions = AI_FEED_CATEGORY_OPTIONS,
    contract = EDITOR_CONTRACT,
    onSubmit,
    onCancel,
}) => {
    const [viewMode, setViewMode] = useState<'ui' | 'json'>('ui')
    const [jsonDraft, setJsonDraft] = useState('')
    const [jsonError, setJsonError] = useState('')
    const [guideOpen, setGuideOpen] = useState(false)
    const [typeGuideOpen, setTypeGuideOpen] = useState(false)

    const hasContent = body.content_blocks?.some(block =>
        Boolean(
            block.text ||
                block.image ||
                block.icon_url ||
                block.blocks?.length ||
                block.segments?.length,
        ),
    )

    const variables = caseVariablesFor(body.category, contract)
    const allVariables = allCaseVariables(contract)

    // 강제 검증: 계약(kinds+common_vars) 밖 {변수}가 있으면 저장을 막는다.
    const allowedVars = allowedVariableNames(body.category, contract)
    const invalidVariables = extractVariableTokens(body.content_blocks).filter(name => !allowedVars.includes(name))
    const canSubmit = hasContent && invalidVariables.length === 0

    const toggleViewMode = () => {
        if (viewMode === 'ui') {
            setJsonDraft(JSON.stringify(formBodyToBlocks(body), null, 2))
            setJsonError('')
            setViewMode('json')
        } else {
            setViewMode('ui')
        }
    }

    // JSON 직접편집: 유효성(파싱 + 배열)만 검사하고 원본을 raw_blocks로 그대로 보관한다(정규화/기본값 강제 없음).
    // content_blocks는 미리보기 렌더용으로만 파싱해 둔다.
    const onChangeJsonDraft = (text: string) => {
        setJsonDraft(text)
        try {
            const parsed = JSON.parse(text)
            if (!Array.isArray(parsed)) throw new Error('배열([...]) 형태의 JSON이어야 합니다.')
            setBody(prev => ({...prev, content_blocks: fromContentBlockJSON(parsed), raw_blocks: parsed}))
            setJsonError('')
        } catch (e) {
            setJsonError(e instanceof Error ? e.message : 'JSON 파싱 오류')
        }
    }

    return (
        <div className={`${className}`}>
            <WizButton type={'black'} text={'목록으로'} onClick={onCancel} className={'mb-[16px]'} />

            <div className={'grid grid-cols-[1fr_420px] gap-x-[24px]'}>
                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[24px]'}>
                    <Text className={'text-h3'}>{view === 'create' ? '새 피드 만들기' : '피드 수정'}</Text>

                    <div className={'flex items-start justify-between gap-x-[12px]'}>
                        <div className={'flex-1 space-y-[8px]'}>
                            <Text className={'text-body3 text-font02'}>Category</Text>
                            <WizSelect
                                customOption={categoryOptions}
                                value={categoryOptions.find(item => item.id === body.category)?.value}
                                disabled={view === 'edit'}
                                onChange={value => {
                                    const category = categoryOptions.find(item => item.value === value)?.id
                                    setBody(prev => ({...prev, category}))
                                }}
                            />
                            {view === 'edit' && (
                                <Text className={'text-ti3 text-font03'}>수정 화면에서는 Category를 변경할 수 없습니다.</Text>
                            )}
                        </div>
                        <button
                            type={'button'}
                            onClick={toggleViewMode}
                            className={'text-ti3 font-bold text-sub_blue underline cursor-pointer whitespace-nowrap mt-[26px]'}>
                            {viewMode === 'ui' ? 'View 전환 (JSON)' : 'View 전환 (UI)'}
                        </button>
                    </div>

                    {viewMode === 'ui' ? (
                        <div className={'space-y-[8px]'}>
                            <div className={'flex items-center justify-between'}>
                                <Text className={'text-body3 text-font02'}>content_blocks</Text>
                                <div className={'flex items-center gap-x-[16px]'}>
                                    <button
                                        type={'button'}
                                        onClick={() => setTypeGuideOpen(true)}
                                        className={'flex items-center gap-x-[4px] text-body3 text-font02 hover:text-font01 cursor-pointer'}>
                                        <IcGuide className={'w-[14px] h-[14px] fill-current'} />
                                        타입 목록 보기
                                    </button>
                                    <button
                                        type={'button'}
                                        onClick={() => setGuideOpen(true)}
                                        className={'flex items-center gap-x-[4px] text-body3 text-font02 hover:text-font01 cursor-pointer'}>
                                        <IcGuide className={'w-[14px] h-[14px] fill-current'} />
                                        변수 목록 보기
                                    </button>
                                </div>
                            </div>
                            <ContentBlockBuilder
                                blocks={body.content_blocks}
                                onChange={next => setBody(prev => ({...prev, content_blocks: next, raw_blocks: undefined}))}
                                variables={variables}
                            />
                        </div>
                    ) : (
                        <div className={'space-y-[8px]'}>
                            <div className={'flex items-center justify-between'}>
                                <Text className={'text-body3 text-font02'}>content_blocks (JSON 직접 편집)</Text>
                                <div className={'flex items-center gap-x-[16px]'}>
                                    <button
                                        type={'button'}
                                        onClick={() => setTypeGuideOpen(true)}
                                        className={'flex items-center gap-x-[4px] text-body3 text-font02 hover:text-font01 cursor-pointer'}>
                                        <IcGuide className={'w-[14px] h-[14px] fill-current'} />
                                        타입 목록 보기
                                    </button>
                                    <button
                                        type={'button'}
                                        onClick={() => setGuideOpen(true)}
                                        className={'flex items-center gap-x-[4px] text-body3 text-font02 hover:text-font01 cursor-pointer'}>
                                        <IcGuide className={'w-[14px] h-[14px] fill-current'} />
                                        변수 목록 보기
                                    </button>
                                </div>
                            </div>
                            <textarea
                                value={jsonDraft}
                                onChange={e => onChangeJsonDraft(e.target.value)}
                                spellCheck={false}
                                className={
                                    'w-full min-h-[320px] border border-line02 rounded-[8px] p-[14px] font-mono text-ti3 leading-[1.6] focus:outline-none focus:border-sub_blue'
                                }
                            />
                            {jsonError && <Text className={'text-ti3 text-font_red'}>{jsonError}</Text>}
                        </div>
                    )}

                    <div className={'space-y-[8px]'}>
                        <Text className={'text-body3 text-font02'}>미리보기</Text>
                        <div
                            className={
                                'border border-line02 rounded-[8px] px-[20px] py-[16px] bg-line04 text-body1 leading-[1.8]'
                            }>
                            <ContentBlocksInlinePreview
                                blocks={body.content_blocks}
                                imageClassName={'block w-[600px] h-auto mx-auto my-[10px] rounded-[6px]'}
                            />
                        </div>
                    </div>

                    {invalidVariables.length > 0 && (
                        <Text className={'text-ti3 text-font_red'}>
                            계약에 없는 변수는 저장할 수 없습니다: {invalidVariables.map(name => `{${name}}`).join(', ')}
                        </Text>
                    )}

                    <div className={'flex justify-end gap-x-[10px]'}>
                        <WizButton type={'cancel'} text={'취소'} onClick={onCancel} />
                        <WizButton type={'primary'} text={'저장'} onClick={onSubmit} disabled={!canSubmit} />
                    </div>
                </div>

                <div
                    className={
                        'sticky top-[20px] self-start bg-white rounded-[10px] shadow-normal p-[20px] max-h-[80vh] overflow-y-auto'
                    }>
                    <Text className={'text-ti1 mb-[10px]'}>content_blocks JSON 미리보기</Text>
                    <pre className={'text-ti3 text-font02 whitespace-pre-wrap break-all'}>
                        {JSON.stringify(formBodyToBlocks(body), null, 2)}
                    </pre>
                </div>
            </div>

            <VariableGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} variables={allVariables} />
            <BlockTypeGuideModal
                open={typeGuideOpen}
                onClose={() => setTypeGuideOpen(false)}
                blockTypes={contract.block_types}
            />
        </div>
    )
}

export default FeedForm
